import type { 
  TemplateType, 
  SectionType, 
  ContentType, 
  LengthTag,
  WebsiteTemplateRule,
  WebsiteSectionRule 
} from '@/types/templateRules';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULT_TEMPLATE_CONFIGS } from './templateConfig';

// Function to get default template rules from database
export async function getDefaultTemplateRules(): Promise<Record<`${TemplateType}:${ContentType}`, Omit<WebsiteTemplateRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>>> {
  const { data: templateTypes } = await supabase
    .from('template_types')
    .select('*');

  const contentTypes: ContentType[] = ['post', 'page'];
  const defaultRules: Record<`${TemplateType}:${ContentType}`, Omit<WebsiteTemplateRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>> = {};

  // Use template types from DB if available, otherwise fall back to DEFAULT_TEMPLATE_CONFIGS
  const effectiveTemplateTypes = templateTypes || DEFAULT_TEMPLATE_CONFIGS;

  effectiveTemplateTypes.forEach(templateType => {
    contentTypes.forEach(contentType => {
      const key = `${templateType.id}:${contentType}` as const;
      defaultRules[key] = {
        template_type: templateType.id as TemplateType,
        content_type: contentType,
        allowed_length_tags: ['ultra-short', 'short', 'medium', 'medium-long', 'long', 'very-long'],
        full_html_example: `<${contentType === 'post' ? 'article' : 'div'} class="${templateType.id}-${contentType}"><h1>${templateType.name} Title</h1><div class="content">...</div></${contentType === 'post' ? 'article' : 'div'}>`
      };
    });
  });

  return defaultRules;
}

// Function to get default section rules from database
export async function getDefaultSectionRules(): Promise<Record<`${TemplateType}:${ContentType}`, Omit<WebsiteSectionRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>[]>> {
  const { data: templateTypes } = await supabase
    .from('template_types')
    .select('*');
  const { data: sectionTypes } = await supabase
    .from('section_types')
    .select('*');

  const contentTypes: ContentType[] = ['post', 'page'];
  const defaultRules: Record<`${TemplateType}:${ContentType}`, Omit<WebsiteSectionRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>[]> = {};

  // Use template types from DB if available, otherwise fall back to DEFAULT_TEMPLATE_CONFIGS
  const effectiveTemplateTypes = templateTypes || DEFAULT_TEMPLATE_CONFIGS;

  effectiveTemplateTypes.forEach(templateType => {
    contentTypes.forEach(contentType => {
      const key = `${templateType.id}:${contentType}` as const;
      defaultRules[key] = sectionTypes?.map(section => ({
        section_id: section.id,
        template_type: templateType.id as TemplateType,
        content_type: contentType,
        enabled: section.id !== 'image' && section.id !== 'quote' && section.id !== 'testimonial', // Disable problematic sections by default
        html_example: `<div class="${section.id}">${section.name} content...</div>`,
        section_notes: section.description || `Default ${section.name} section`
      })) || [];
    });
  });

  return defaultRules;
}

// Export empty objects as placeholders - they will be populated when the functions are called
export const DEFAULT_TEMPLATE_RULES: Record<`${TemplateType}:${ContentType}`, Omit<WebsiteTemplateRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>> = {};
export const DEFAULT_SECTION_RULES: Record<`${TemplateType}:${ContentType}`, Omit<WebsiteSectionRule, 'id' | 'website_id' | 'created_at' | 'updated_at'>[]> = {}; 